import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { registerServiceWorker } from "@/lib/registerSW";

// Patch ResizeObserver to prevent the "loop completed with undelivered notifications" error
// This is a benign error from Radix UI components that CRA's error overlay catches
if (typeof window !== 'undefined') {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        // Wrap callback in requestAnimationFrame to prevent the loop error
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };
}

// Top-level error boundary — catches React rendering crashes.
// Module-level TDZ crashes are handled by the inline script in index.html.
class TopLevelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isStaleCache: false };
  }

  static getDerivedStateFromError(error) {
    const msg = (error?.message || '') + ' ' + (error?.name || '');
    const isStaleCache = (
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk') ||
      msg.includes('dynamically imported module') ||
      msg.includes('before initialization') ||
      (error instanceof SyntaxError)
    );
    return { hasError: true, isStaleCache };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[TopLevelErrorBoundary] Fatal crash:', error, errorInfo);
    // Only nuke for stale-cache errors. Runtime errors should not
    // trigger cache clearing — it cannot fix them and causes a loop.
    if (this.state.isStaleCache && typeof window.nukeAndReload === 'function') {
      window.nukeAndReload();
    }
  }

  render() {
    if (this.state.hasError && !this.state.isStaleCache) {
      // Runtime error — show error UI (NOT blank page)
      return React.createElement('div', {
        style: {
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          minHeight: '100vh', background: '#f9fafb', padding: '16px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }
      },
        React.createElement('div', { style: { textAlign: 'center', maxWidth: '400px' } },
          React.createElement('p', {
            style: { color: '#374151', fontSize: '16px', marginBottom: '8px' }
          }, 'Something went wrong.'),
          React.createElement('p', {
            style: { color: '#6b7280', fontSize: '14px', marginBottom: '16px' }
          }, 'Please try refreshing the page.'),
          React.createElement('button', {
            onClick: () => window.location.reload(),
            style: {
              padding: '12px 32px', background: '#3b82f6', color: '#fff',
              border: 'none', borderRadius: '8px', fontSize: '16px',
              cursor: 'pointer', fontFamily: 'inherit', marginRight: '8px'
            }
          }, 'Refresh'),
          React.createElement('button', {
            onClick: () => { window.location.href = '/'; },
            style: {
              padding: '12px 32px', background: '#e5e7eb', color: '#374151',
              border: 'none', borderRadius: '8px', fontSize: '16px',
              cursor: 'pointer', fontFamily: 'inherit'
            }
          }, 'Go Home')
        )
      );
    }
    if (this.state.hasError) {
      // Stale-cache error — nukeAndReload handles the UI
      return null;
    }
    return this.props.children;
  }
}

// Render the app first - PWA registration happens after
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <TopLevelErrorBoundary>
      <App />
    </TopLevelErrorBoundary>
  </React.StrictMode>,
);

// Register Service Worker for PWA AFTER app renders
// This ensures the app loads even if SW registration fails
// Wrapped in setTimeout to not block initial render
setTimeout(() => {
  try {
    registerServiceWorker();
  } catch (e) {
    console.log('[SW] Service worker registration failed:', e);
  }
}, 1000);
